// EmotionTimeline — a presentational vocal-emotion overlay for a captured transcript (CAP-2.3).
//
// SIGNAL, NOT TRUTH. This renders *inferred vocal tone* aligned to transcript utterances. It is a
// soft signal for advisers, never a fact about anyone's emotional state, and the UI says so loudly.
//
// Pure / presentational: it fetches nothing. The hosting page reads the rows and passes them in.
//   • `utterances`  — the transcript windows ({ start,end in SECONDS, speaker, text }).
//   • `emotions`    — research_source_emotion rows ({ speaker, start_ms, end_ms, emotions[] top-first }).
// Each emotion segment is aligned to the utterance window(s) it overlaps by time
// ([start_ms,end_ms] vs utterance [start*1000,end*1000]); we render a compact per-utterance strip:
// the top emotion label + a small intensity bar (a div whose width encodes the top score).
//
// CONSENT GATE: the caller only ever passes emotion rows when the source has a live consent row with
// emotion_inference_ok=true (RLS already enforces SELECT). A non-consented / non-entitled reader gets
// `emotions = []`, so this component renders NOTHING (returns null) — graceful, no error, no caption.
//
// CLIENT-VOICE SUPPRESSION — what is actually implemented here, honestly:
//   The product intent is to suppress the emotion strip for any utterance spoken by a CLIENT / external
//   voice (only advisers' inferred tone should ever surface). The reliable signal for "this speaker is a
//   client" is per-speaker voiceprint speaker-role data (research_speaker_labels: a speaker_int → an
//   internal user_id for advisers, null/voiceprint for externals). At build time that table is EMPTY
//   and carries no explicit adviser/client role column, so a *per-speaker* client/adviser split cannot
//   be derived reliably. Rather than guess (and risk surfacing a client's inferred emotion), we take the
//   conservative stance the spec calls for: when no reliable per-speaker role map is supplied, the WHOLE
//   overlay is gated to advisers — i.e. the host decides entitlement (it already does, via the consent
//   RLS that only returns rows to read-capable advisers), and we render a one-line note documenting that
//   per-speaker client suppression is pending the voiceprint speaker-role data. If/when the host CAN
//   supply a per-speaker role map (`speakerRoles`), we honour it and suppress client utterances
//   individually, dropping the global note. See `speakerRoles` below.

import { useMemo } from 'react';
import { ShieldCheck, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public shapes
// ---------------------------------------------------------------------------

/** A transcript window. `start`/`end` are SECONDS (Deepgram utterance convention). */
export interface Utterance {
  start: number;
  end: number;
  speaker: number | string | null;
  text: string;
}

/** One scored emotion (already normalised 0..1 or 0..100; we treat it as a relative intensity). */
export interface EmotionScore {
  name: string;
  score: number;
}

/** A research_source_emotion row, normalised. `emotions` is top-first. */
export interface EmotionSegment {
  speaker: string | null;
  startMs: number;
  endMs: number;
  emotions: EmotionScore[];
}

/**
 * Optional per-speaker role map keyed by the utterance speaker id (stringified). When supplied AND
 * trustworthy, we suppress the strip for any utterance whose speaker is 'client'/'external' and we
 * drop the global "gated to advisers" note. When omitted (the live default — the voiceprint
 * speaker-role table is not yet populated), the whole overlay stays adviser-gated and the note shows.
 */
export type SpeakerRole = 'adviser' | 'client' | 'external' | 'unknown';

export interface EmotionTimelineProps {
  utterances: Utterance[];
  emotions: EmotionSegment[];
  /** See SpeakerRole. Optional; absent in the current data model. */
  speakerRoles?: Record<string, SpeakerRole>;
  className?: string;
}

// ---------------------------------------------------------------------------
// Alignment helpers
// ---------------------------------------------------------------------------

// Two [a,b] ranges (ms) overlap when they share any instant. Strict-positive overlap so adjacent
// (end === next.start) windows don't bleed.
function overlapsMs(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

// Clamp a relative intensity to a 0..1 fraction for the bar width. Scores may arrive as 0..1 or
// 0..100; treat anything > 1 as a percentage. Never throws; out-of-range → clamped.
function intensityFraction(score: number): number {
  if (!Number.isFinite(score)) return 0;
  const f = score > 1 ? score / 100 : score;
  if (f < 0) return 0;
  if (f > 1) return 1;
  return f;
}

function speakerKey(speaker: number | string | null): string {
  if (speaker == null) return '';
  return String(speaker);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmotionTimeline({
  utterances,
  emotions,
  speakerRoles,
  className,
}: EmotionTimelineProps) {
  // Whether we have a trustworthy per-speaker role map. When we do, we suppress client/external
  // utterances individually; when we don't, the whole overlay is adviser-gated (see header note).
  const hasRoleMap = !!speakerRoles && Object.keys(speakerRoles).length > 0;

  // For each utterance index, the best (top, highest-score) emotion across all overlapping segments.
  // O(utterances × segments) — both are small per source; no memo-busting object identities created.
  const topByUtterance = useMemo(() => {
    const result: Array<EmotionScore | null> = [];
    for (const u of utterances) {
      const uStartMs = u.start * 1000;
      const uEndMs = u.end * 1000;
      let best: EmotionScore | null = null;
      for (const seg of emotions) {
        if (!overlapsMs(uStartMs, uEndMs, seg.startMs, seg.endMs)) continue;
        const top = seg.emotions[0];
        if (!top) continue;
        if (best == null || top.score > best.score) best = top;
      }
      result.push(best);
    }
    return result;
  }, [utterances, emotions]);

  // CONSENT / ENTITLEMENT GATE: no emotion rows → render nothing at all (graceful). This is the
  // non-consented / non-entitled reader path: RLS returned [] and the overlay must be invisible.
  if (emotions.length === 0) return null;

  // If, after alignment, nothing actually overlaps a transcript window, there's no strip to show.
  const anyAligned = topByUtterance.some((e) => e != null);
  if (!anyAligned) return null;

  return (
    <section
      className={cn('rounded-xl border border-border bg-card p-4', className)}
      aria-label="Inferred vocal tone timeline"
    >
      {/* MANDATORY framing — consent badge + signal-not-truth caption. */}
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
          <ShieldCheck className="h-3 w-3" aria-hidden="true" />
          Emotion analysis consented
        </span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Signal, not truth — inferred vocal tone, not a fact
        </span>
      </div>

      {/* When we have no reliable per-speaker role map, document that per-speaker client suppression
          is pending the voiceprint speaker-role data, and note the whole overlay is adviser-gated. */}
      {!hasRoleMap && (
        <p className="mb-3 flex items-start gap-1.5 rounded-lg bg-muted/40 p-2 text-[11px] leading-snug text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>
            Adviser-only view. Per-speaker client suppression needs the voiceprint speaker-role data,
            which isn’t populated yet — so tone is shown for the whole transcript, gated to advisers.
          </span>
        </p>
      )}

      <ul className="space-y-2">
        {utterances.map((u, i) => {
          const top = topByUtterance[i];
          if (!top) return null; // no overlapping emotion for this window → no strip

          // Per-speaker suppression (only possible with a trustworthy role map). A client/external
          // voice never has its inferred tone surfaced.
          if (hasRoleMap) {
            const role = speakerRoles![speakerKey(u.speaker)] ?? 'unknown';
            if (role === 'client' || role === 'external') return null;
          }

          const frac = intensityFraction(top.score);
          const pct = Math.round(frac * 100);
          const speakerLabel =
            u.speaker == null ? null : `Speaker ${String(u.speaker)}`;

          return (
            <li key={i} className="flex items-center gap-2">
              {speakerLabel && (
                <span className="w-16 shrink-0 truncate text-[11px] text-muted-foreground/70">
                  {speakerLabel}
                </span>
              )}
              <span className="w-20 shrink-0 truncate text-xs font-medium capitalize">
                {top.name}
              </span>
              {/* Intensity bar — a div width %. Decorative; the label carries the meaning. */}
              <span
                className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted"
                role="img"
                aria-label={`${top.name}, intensity ${pct}%`}
              >
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-primary/70"
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
              </span>
              <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground/70">
                {pct}%
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default EmotionTimeline;
